import { fetcherWithToken, REVALIDATE } from '@/shared/api/fetcher';
import { useState, useEffect, useMemo } from 'react';
import useSWRMutation from 'swr/mutation';
import useSWR from 'swr';
import { useRouter } from 'next/navigation';
import { format } from 'date-fns';
import { useUser } from '@/shared/providers/user-provider/use-user';
import { useNotifications } from '@/shared/providers/notifications-provider/notifications-context';
import { groupTransactionsByDate } from './utils/groupTransactionsByDate';
import { transformAddress } from './utils/transformAddress';
import { useI18n } from '@locales/client';

async function getPayoutLimit(url) {
  return fetcherWithToken(url, {
    method: 'GET',
  });
}

async function getPayoutEstimate(url, { arg }: { arg: string }) {
  const params = new URLSearchParams(arg);
  return fetcherWithToken(`${url}?${params}`, {
    method: 'GET',
  });
}

async function createPayment(url, { arg }: { arg: string }) {
  return fetcherWithToken(url, {
    method: 'POST',
    body: JSON.stringify(arg),
  });
}

async function deleteUserAdresses(url, { arg }: { arg: string }) {
  return fetcherWithToken(url, {
    method: 'DELETE',
    body: JSON.stringify(arg),
  });
}

const deleteMutationHandler = (section: string) => {
  return async (url: string, { arg: key }: { arg: string }) => {
    const fullUrl = `${url}?section=${section}&key=${key}`;
    return deleteUserAdresses(fullUrl, { arg: key });
  };
};

async function createFiatPayment(url, { arg }: { arg: string }) {
  return fetcherWithToken(url, {
    method: 'POST',
    body: JSON.stringify(arg),
  });
}

async function createDepositFiat(url, { arg }) {
  return fetcherWithToken(url, {
    method: 'POST',
    body: JSON.stringify(arg),
  });
}

const useWithdrawForm = (currency) => {
  const {
    data: limitCurrency,
    trigger: payoutLimitTrigger,
    error: payoutLimiterror,
  } = useSWRMutation(
    `/payment/crypto/payout/limit?currency=${currency?.code}`,
    getPayoutLimit
  );

  const { trigger: payoutEstimateTrigger, error: payoutEstimateError } =
    useSWRMutation('/payment/crypto/payout/estimate', getPayoutEstimate);

  const { trigger: createFiatPaymentTrigger, error: fiatError } =
    useSWRMutation('/payment/fiat/payout', createFiatPayment);

  const {
    data: savedAdresses,
    mutate: mutateSavedAdresses,
    error: savedAdressesError,
  } = useSWR('/payment/userAddresses ', fetcherWithToken, REVALIDATE);

  const { trigger: createPaymentTrigger, error: createPaymentError } =
    useSWRMutation('/payment/crypto/payout', createPayment);

  const {
    trigger: deleteUserAdressesTrigger,
    error: userDeleteAddressesError,
  } = useSWRMutation('/payment/userAddresses', deleteUserAdresses);

  const {
    trigger: createUserAddressesTrigger,
    error: userCreateAddressesError,
  } = useSWRMutation('/payment/userAddresses', createPayment);

  const [addresses, setAddresses] = useState([]);
  const [isLoadingLimited, setIsLoadingLimited] = useState(true);

  useEffect(() => {
    if (savedAdresses && currency && currency.code) {
      const transformAddresses = savedAdresses
        .map((address) => {
          return {
            ...address,
            address: transformAddress(address.address),
            originalAddress: address.address,
          };
        })
        .filter((address) => address.currency === currency.code);

      setAddresses(transformAddresses);
    }
    setAddresses(savedAdresses);
  }, [savedAdresses]);

  useEffect(() => {
    if (currency?.code) {
      setIsLoadingLimited(true);
      const fetchPayoutLimit = async () => {
        try {
          await payoutLimitTrigger();
        } catch (error) {
          // Handle error if needed
        } finally {
          setIsLoadingLimited(false);
        }
      };
      fetchPayoutLimit();
    }
  }, [currency]);

  const transformTransaction = (transaction, locale) => {
    const originalDate = new Date(transaction.date * 1000);

    let atString;
    if (locale === 'ru') {
      atString = 'в';
    } else {
      atString = 'at';
    }

    if (transaction.address) {
      return {
        ...transaction,
        address: transformAddress(transaction.address),
        originalAddress: transaction.address,
        dateDetail: format(originalDate, `d MMMM HH:mm`),
      };
    }

    return {
      ...transaction,
      dateDetail: format(originalDate, `d MMMM HH:mm`),
    };
  };

  const saveAddress = async (paramsSave) => {
    try {
      await createUserAddressesTrigger(paramsSave);
      await mutateSavedAdresses();
    } catch (error) {
      // console.error('Error saving user address:', error);
    }
  };

  return {
    payoutLimitTrigger,
    limitCurrency,
    payoutEstimateTrigger,
    savedAdresses: addresses,
    createFiatPaymentTrigger,
    transformTransaction,
    createPaymentTrigger,
    deleteUserAdressesTrigger,
    mutateSavedAdresses,
    createUserAddressesTrigger,
    saveAddress,
    isLoadingLimited,
  };
};

const useWalletHistory = (transaction) => {
  const {
    data,
    error: walletHistoryError,
    mutate: mutateWalletHistory,
    isLoading,
  } = useSWR('/payment/history', fetcherWithToken, REVALIDATE);
  const [walletHistory, setWalletHistory] = useState([]);
  const t = useI18n();
  const locale = t('code');
  const { user } = useUser();
  const { notifications } = useNotifications();

  useEffect(() => {
    if (!data) return;
    const updatedData = notifications?.transaction
      ? data.map((t) =>
          t.id === notifications.transaction.id
            ? { ...t, status: notifications.transaction.status }
            : t
        )
      : [...data];
    setWalletHistory(groupTransactionsByDate(updatedData, locale));
  }, [data, locale, notifications]);

  useEffect(() => {
    if (transaction && transaction.type === 'withdraw') {
      mutateWalletHistory();
    }
  }, [transaction, mutateWalletHistory]);

  return {
    walletHistory,
    isLoading,
    walletHistoryError,
    fetchWalletHistory: mutateWalletHistory,
  };
};

const useFiatForm = ({ bonus, visible }) => {
  const url = `/payment/fiat/payment/methods${bonus?.id ? `?bonus=${bonus?.id}` : ''}`;

  const {
    data: paymentMethods,
    isLoading: isLoadingPaymentMethods,
    error: errorPaymentMethods,
    mutate,
  } = useSWR(visible ? url : null, fetcherWithToken, REVALIDATE);

  const {
    data: payoutMethods,
    isLoading: isLoadingPayoutMethods,
    error: errorPayoutMethods,
  } = useSWR(visible ? `/payment/fiat/payout/methods` : null, fetcherWithToken);

  const { trigger } = useSWRMutation(
    '/payment/fiat/payment',
    createDepositFiat
  );

  const isLoading = isLoadingPaymentMethods || isLoadingPayoutMethods;
  const error = errorPaymentMethods || errorPayoutMethods;

  return {
    paymentMethods,
    payoutMethods,
    trigger,
    isLoading,
    error,
  };
};

const useFormSave = (section: string) => {
  const urlGet = `/payment/fiat/getFields?section=${section}`;
  const baseUrlDelete = `/payment/fiat/deleteFields`;

  const {
    data: savePay,
    isLoading: isLoadingSavePay,
    mutate: mutateSavePay,
    error: errorSavePay,
  } = useSWR(section ? urlGet : null, fetcherWithToken, REVALIDATE);

  const { trigger: deleteSavePay, error: userDeleteSavePay } = useSWRMutation(
    baseUrlDelete,
    deleteMutationHandler(section)
  );

  const handleDeleteSavePay = (key: string) => {
    if (!key || !section) {
      console.error('Key or section is missing', { key, section });
      return Promise.reject(new Error('Key and section are required'));
    }
    return deleteSavePay(key);
  };

  return {
    savePay,
    isLoadingSavePay,
    mutateSavePay,
    handleDeleteSavePay,
    userDeleteSavePay,
    errorSavePay,
  };
};

interface PostRedirectResponse {
  paymentID?: string;
  url?: string;
  post?: Record<string, string | number | boolean>;
}
const usePostRedirect = () => {
  const router = useRouter();

  const executeRedirect = (
    response: PostRedirectResponse,
    unlock: () => void
  ) => {
    if (!response?.url) return;

    // Если есть данные для POST-редиректа
    if (response.post && typeof response.post === 'object') {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = response.url;
      form.style.display = 'none';
      form.id = String(response.paymentID);

      // Добавляем параметры
      Object.entries(response.post).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = String(value);
        form.appendChild(input);
      });

      document.body.appendChild(form);

      const formToSubmit = document.getElementById(String(response.paymentID));
      if (formToSubmit && formToSubmit instanceof HTMLFormElement) {
        formToSubmit.submit();
      }

      // form.submit();
    } else {
      let timer: ReturnType<typeof setTimeout> | null = null;
      let isRedirectCompleted = false;

      // Функция для отмены редиректа
      const cancelRedirect = () => {
        if (isRedirectCompleted) return;

        // 1. Остановить навигацию
        window.stop();

        // 2. Вернуть пользователя на исходную позицию
        window.history.replaceState(null, '', window.location.href);

        unlock();
      };

      // Обработчик успешного завершения загрузки
      window.addEventListener('load', () => {
        isRedirectCompleted = true;
        if (timer) clearTimeout(timer);
      });

      // Запускаем таймер отмены
      timer = setTimeout(cancelRedirect, 8000);

      try {
        window.location.href = response.url;
      } catch (e) {
        if (timer) clearTimeout(timer);
        console.error('Redirect error:', e);
      }
    }
  };

  return executeRedirect;
};

interface UseAddressMatchingProps {
  inputValue: string;
  savedAddresses: any[];
  addressKey?: string;
  getAddress?: (item: any) => string;
  onEdit: (item: any | null) => void;
}

const useAddressSelection = ({
  inputValue,
  savedAddresses,
  addressKey = '',
  getAddress,
  onEdit,
}: UseAddressMatchingProps) => {
  // Функция для извлечения адреса из элемента
  const extractAddress = (item: any): string => {
    if (getAddress) return getAddress(item);
    return item[addressKey] || '';
  };

  // Поиск совпадений
  const { fullMatchItem, partialMatchItem } = useMemo(() => {
    let fullMatch = null;
    let partialMatch = null;

    // Удаляем подчеркивания и обрезаем пробелы
    const cleanedInput = inputValue.replace(/_/g, '').trim();

    if (cleanedInput && savedAddresses?.length) {
      const inputUpper = cleanedInput.toUpperCase();

      for (const item of savedAddresses) {
        const address = extractAddress(item);
        const addressUpper = address.trim().toUpperCase();

        if (addressUpper === inputUpper) {
          fullMatch = item;
          break;
        }

        if (
          !partialMatch &&
          inputUpper.length >= 4 &&
          addressUpper.startsWith(inputUpper)
        ) {
          partialMatch = item;
        }
      }
    }

    return { fullMatchItem: fullMatch, partialMatchItem: partialMatch };
  }, [inputValue, savedAddresses]);

  // Управление состоянием выбранного адреса
  const [selectedAddress, setSelectedAddress] = useState<string | null>(null);
  const [blinkingAddress, setBlinkingAddress] = useState<string | null>(null);

  useEffect(() => {
    if (fullMatchItem) {
      const address = extractAddress(fullMatchItem);
      setSelectedAddress(address);
      setBlinkingAddress(null);
      onEdit(fullMatchItem);
    } else if (partialMatchItem) {
      const address = extractAddress(partialMatchItem);
      setSelectedAddress(address);
      setBlinkingAddress(address);
    } else {
      setSelectedAddress(null);
      setBlinkingAddress(null);
      onEdit(null);
    }
  }, [fullMatchItem, partialMatchItem]);

  return {
    fullMatchItem,
    partialMatchItem,
    selectedAddress,
    blinkingAddress,
    setSelectedAddress,
    setBlinkingAddress,
  };
};

export {
  useWithdrawForm,
  useWalletHistory,
  useFiatForm,
  usePostRedirect,
  useFormSave,
  useAddressSelection,
};
